import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  uploadConnectionsCSV,
  getUploadStatus,
  getStoredUploadJobId,
  setStoredUploadJobId,
  populateConnectionsScores,
  deleteDetailedConnections,
  deleteConnectionsScores,
} from '../services/connectionsService';
import ConnectionsListView from './ConnectionsListView';
import CompaniesToTargetView from './CompaniesToTargetView';

type View = 'upload' | 'connections' | 'companies';

type DeleteTarget = 'connections' | 'scores' | 'all';

type PipelineStage = 'idle' | 'parsing' | 'enriching' | 'saving' | 'scores' | 'done';

const POLL_INTERVAL_MS = 3000;
const PIPELINE_STAGES: { key: PipelineStage; label: string }[] = [
  { key: 'parsing', label: 'Parsing CSV' },
  { key: 'enriching', label: 'LLM enrichment' },
  { key: 'saving', label: 'Saving to Firestore' },
  { key: 'scores', label: 'Calculating company scores' },
];

const mapStageFromStatus = (
  stage?: string,
  message?: string
): PipelineStage => {
  if (stage === 'parsing') return 'parsing';
  if (stage === 'enriching') return 'enriching';
  if (stage === 'saving') return 'saving';
  if (stage === 'scores') return 'scores';
  if (stage === 'done') return 'done';
  const normalized = (message || '').toLowerCase();
  if (normalized.includes('parsing') || normalized.includes('already-analyzed')) return 'parsing';
  if (normalized.includes('enriching')) return 'enriching';
  if (normalized.includes('saving')) return 'saving';
  if (normalized.includes('score')) return 'scores';
  return 'enriching';
};

const ConnectionsDashboard: React.FC = () => {
  const { user, userProfile, isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewFromUrl = searchParams.get('view') as View | null;
  const companyFromUrl = searchParams.get('company');
  const [currentView, setCurrentView] = useState<View>(
    viewFromUrl && ['upload', 'connections', 'companies'].includes(viewFromUrl) ? viewFromUrl : 'upload'
  );
  const highlightCompany = companyFromUrl ? decodeURIComponent(companyFromUrl) : null;

  // Sync view when URL params change (e.g. deep link from job card)
  useEffect(() => {
    if (viewFromUrl && ['upload', 'connections', 'companies'].includes(viewFromUrl)) {
      setCurrentView(viewFromUrl);
    }
  }, [viewFromUrl]);
  const [file, setFile] = useState<File | null>(null);
  const [limit, setLimit] = useState<number>(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [pipelineStage, setPipelineStage] = useState<PipelineStage>('idle');
  const [processingScores, setProcessingScores] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteTarget | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState<string | null>(null);
  const [asyncJobId, setAsyncJobId] = useState<string | null>(getStoredUploadJobId());
  const [asyncMessage, setAsyncMessage] = useState<string | null>(null);
  const [asyncProgress, setAsyncProgress] = useState<number>(0);
  const [asyncDiagnostics, setAsyncDiagnostics] = useState<string | null>(null);
  const [uploadProcessed, setUploadProcessed] = useState<number | null>(null);
  const [uploadTotal, setUploadTotal] = useState<number | null>(null);
  const [uploadCsvTotal, setUploadCsvTotal] = useState<number | null>(null);  // total rows in uploaded CSV
  const [uploadEnrichCount, setUploadEnrichCount] = useState<number | null>(null);  // exec subset going to LLM
  const [llmBatchCompleted, setLlmBatchCompleted] = useState<number | null>(null);
  const [llmBatchTotal, setLlmBatchTotal] = useState<number | null>(null);
  const [firestoreSaved, setFirestoreSaved] = useState<number | null>(null);
  const [firestoreTotal, setFirestoreTotal] = useState<number | null>(null);
  const [notifyEmail, setNotifyEmail] = useState<string>('');
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollErrorCountRef = useRef(0);

  useEffect(() => {
    if (notifyEmail) return;
    const candidate = userProfile?.email || user?.email || '';
    if (candidate) setNotifyEmail(candidate);
  }, [userProfile?.email, user?.email, notifyEmail]);

  // Poll for async upload status when job id is present (survives page reload)
  useEffect(() => {
    if (!asyncJobId || !user) return;
    pollErrorCountRef.current = 0;
    const poll = async () => {
      try {
        const token = await user.getIdToken();
        const status = await getUploadStatus(token, asyncJobId);
        pollErrorCountRef.current = 0;
        setAsyncMessage(status.message ?? status.status);
        setAsyncDiagnostics(null);
        setPipelineStage(mapStageFromStatus(status.stage, status.message));
        // Running counter — surface processed/total from in-progress status
        if (status.processedConnections != null) setUploadProcessed(status.processedConnections);
        if (status.totalConnections != null) setUploadTotal(status.totalConnections);
        if (status.csvTotal != null) setUploadCsvTotal(status.csvTotal);
        if (status.enrichCount != null) setUploadEnrichCount(status.enrichCount);
        if (status.llmBatchCompleted != null) setLlmBatchCompleted(status.llmBatchCompleted);
        if (status.llmBatchTotal != null) setLlmBatchTotal(status.llmBatchTotal);
        if (status.firestoreSaved != null) setFirestoreSaved(status.firestoreSaved);
        if (status.firestoreTotal != null) setFirestoreTotal(status.firestoreTotal);
        setAsyncProgress(
          typeof status.progress === 'number'
            ? Math.max(0, Math.min(100, status.progress))
            : status.status === 'completed'
            ? 100
            : 0
        );
        if (status.status === 'completed') {
          const result = status.result;
          const processed = result?.processedConnections ?? status.processedConnections ?? 0;
          const total = result?.totalConnections ?? status.totalConnections;
          const processLimit = result?.processLimit;
          const skipped = result?.skipped ?? 0;
          const started = status.startedAt ? new Date(status.startedAt).getTime() : 0;
          const completed = status.completedAt ? new Date(status.completedAt).getTime() : Date.now();
          const durationSec = started ? Math.round((completed - started) / 1000) : 0;
          const limitMsg = processLimit != null && total != null && processed < total
            ? ` ${processed} of ${total} processed (limit ${processLimit} applied)`
            : '';
          const skipMsg = skipped > 0 ? ` (${skipped} already analyzed, skipped)` : '';
          const benchmark = result?.benchmarks ?? status.benchmarks;
          const stageDurations = benchmark?.stageDurationsSec ?? {};
          const benchmarkParts = [
            stageDurations.parsing ? `parse ${Math.round(stageDurations.parsing)}s` : null,
            stageDurations.enriching ? `enrich ${Math.round(stageDurations.enriching)}s` : null,
            stageDurations.saving ? `save ${Math.round(stageDurations.saving)}s` : null,
            stageDurations.scores ? `scores ${Math.round(stageDurations.scores)}s` : null,
          ].filter(Boolean);
          const benchMsg = benchmarkParts.length > 0 ? ` Benchmarks: ${benchmarkParts.join(' | ')}.` : '';
          const costUsd = benchmark?.estimatedCostUsd;
          const costMsg = costUsd != null ? ` Est. cost: $${costUsd.toFixed(4)}.` : '';
          const emailStatus = status.emailNotification;
          const emailDetail = status.emailNotificationDetail ? ` (${status.emailNotificationDetail})` : '';
          const emailMsg =
            emailStatus === 'sent'
              ? ' Email notification sent.'
              : emailStatus
              ? ` Email notification status: ${emailStatus}${emailDetail}.`
              : '';
          setSuccess(
            `Successfully processed ${processed} connections in ${durationSec}s${limitMsg}${skipMsg}.${benchMsg}${costMsg}${emailMsg} You can leave or reload anytime.`
          );
          setPipelineStage('done');
          setStoredUploadJobId(null);
          setAsyncJobId(null);
          setAsyncMessage(null);
          setAsyncProgress(0);
          setAsyncDiagnostics(null);
          setUploadProcessed(null);
          setUploadTotal(null);
          setLlmBatchCompleted(null);
          setLlmBatchTotal(null);
          setUploading(false);
          setProcessingScores(false);
          setFile(null);
          setTimeout(() => {
            setCurrentView('companies');
            setSuccess(null);
            setPipelineStage('idle');
          }, 3000);
        } else if (status.status === 'failed') {
          setError(status.error || 'Upload failed');
          setStoredUploadJobId(null);
          setAsyncJobId(null);
          setAsyncMessage(null);
          setAsyncProgress(0);
          setAsyncDiagnostics(null);
          setUploadProcessed(null);
          setUploadTotal(null);
          setLlmBatchCompleted(null);
          setLlmBatchTotal(null);
          setUploading(false);
          setProcessingScores(false);
          setPipelineStage('idle');
        } else {
          pollTimeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
        }
      } catch (err: any) {
        pollErrorCountRef.current += 1;
        const msg = err?.message || 'Failed to fetch status';
        const retries = pollErrorCountRef.current;
        const isJobGone = msg.includes('404') || msg.includes('Job not found');
        if (isJobGone && retries >= 2) {
          setError('The server restarted and the job was lost. Please try again.');
          setStoredUploadJobId(null);
          setAsyncJobId(null);
          setAsyncMessage(null);
          setAsyncDiagnostics(null);
          setUploading(false);
          setProcessingScores(false);
          setPipelineStage('idle');
          return;
        }
        const backoffMs = Math.min(15000, POLL_INTERVAL_MS * Math.max(1, retries));
        if (isJobGone) {
          setAsyncMessage('Status temporarily unavailable. Retrying...');
        } else {
          setAsyncMessage('Temporary status check issue. Retrying...');
        }
        setAsyncDiagnostics(`Status poll retry ${retries} in ${Math.round(backoffMs / 1000)}s: ${msg}`);
        pollTimeoutRef.current = setTimeout(poll, backoffMs);
      }
    };
    poll();
    return () => {
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
  }, [asyncJobId, user]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    setFile(selectedFile);
    setError(null);
    setSuccess(null);
  };

  const handleUpload = async () => {
    if (!file || !user) {
      setError('Please select a file and ensure you are logged in');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);
    setPipelineStage('parsing');

    try {
      console.log('[Upload] Requesting Firebase ID token…');
      const token = await Promise.race([
        user.getIdToken(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('getIdToken timed out after 10s')), 10000)
        ),
      ]);
      console.log('[Upload] Token obtained, sending to backend at', `${process.env.REACT_APP_API_URL || ''}/api/connections/upload`);
      const response = await uploadConnectionsCSV(file, token, limit, true, notifyEmail);

      // All already analyzed (no job created)
      if ('status' in response && response.status === 'completed' && (response as { processedConnections?: number }).processedConnections === 0) {
        setSuccess((response as { message?: string }).message || 'All connections already analyzed.');
        setUploading(false);
        setPipelineStage('idle');
        setFile(null);
        return;
      }

      // Async mode: response has jobId
      if ('jobId' in response) {
        setAsyncJobId(response.jobId);
        setStoredUploadJobId(response.jobId);
        setAsyncMessage('Processing started. You can leave or reload this page.');
        setAsyncDiagnostics(null);
        setPipelineStage('parsing');
        setAsyncProgress(5);
        setFile(null);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return; // Polling effect will handle completion
      }

      // Sync mode (async=false): response has processedConnections
      setPipelineStage('saving');
      await new Promise((r) => setTimeout(r, 300));
      setPipelineStage('scores');
      setProcessingScores(true);
      let scoreErrorOccurred = false;
      try {
        await populateConnectionsScores(token);
      } catch (scoreError: any) {
        scoreErrorOccurred = true;
        setError(`Upload successful but score calculation failed: ${scoreError?.message || 'Unknown error'}`);
      }
      setProcessingScores(false);
      setPipelineStage('done');
      if (!scoreErrorOccurred) {
        setSuccess(`Successfully processed ${response.processedConnections} connections! Scores have been calculated.`);
        setTimeout(() => {
          setFile(null);
          setPipelineStage('idle');
          setCurrentView('companies');
        }, 2000);
      }
    } catch (err: any) {
      const errorMessage = err?.message || err?.toString() || 'Failed to upload connections';
      setError(errorMessage);
      setPipelineStage('idle');
      setProcessingScores(false);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (target: DeleteTarget) => {
    if (!user) return;
    setDeleting(true);
    setDeleteMessage(null);
    setError(null);
    try {
      const token = await user.getIdToken();
      if (target === 'connections' || target === 'all') {
        const res = await deleteDetailedConnections(token);
        setDeleteMessage(res.message);
      }
      if (target === 'scores' || target === 'all') {
        const res = await deleteConnectionsScores(token);
        setDeleteMessage((prev) => (prev ? `${prev}. ${res.message}` : res.message));
      }
      setDeleteConfirm(null);
      setTimeout(() => setDeleteMessage(null), 5000);
    } catch (err: any) {
      setError(err?.message || 'Delete failed');
      setDeleteConfirm(null);
    } finally {
      setDeleting(false);
    }
  };

  const deleteConfirmConfig: Record<DeleteTarget, { title: string; description: string }> = {
    connections: {
      title: 'Delete all your connections',
      description: 'This will permanently delete all connection documents that belong to you. Only your data is removed; you can re-upload a CSV to add them back.',
    },
    scores: {
      title: 'Delete all your company scores',
      description: 'This will delete all company score documents that belong to you. Run "Upload & Process" or populate scores again to recalculate from your connections.',
    },
    all: {
      title: 'Delete all your connections data',
      description: 'This will permanently delete all your connection records and all your company score documents. Only your data is affected; you can re-upload a CSV to start over.',
    },
  };

  return (
    <div className="min-h-screen bg-ts-cream">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Tabs */}
        <div className="mb-8">
          <div className="bg-white rounded-lg shadow-sm p-1 inline-flex">
            <button
              onClick={() => {
                setCurrentView('upload');
                setSearchParams({});
              }}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${
                currentView === 'upload'
                  ? 'bg-ts-navy text-white'
                  : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              Upload CSV
            </button>
            <button
              onClick={() => {
                setCurrentView('companies');
                setSearchParams({ view: 'companies', ...(highlightCompany ? { company: highlightCompany } : {}) });
              }}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${
                currentView === 'companies'
                  ? 'bg-ts-navy text-white'
                  : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              Companies to Target
            </button>
            <button
              onClick={() => {
                setCurrentView('connections');
                setSearchParams({ view: 'connections' });
              }}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${
                currentView === 'connections'
                  ? 'bg-ts-navy text-white'
                  : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              My Connections
            </button>
          </div>
        </div>

        {/* Content Area */}
        {/* Async upload in progress banner (shown on any tab when job is running) */}
        {asyncJobId && (() => {
          const stageKeys = PIPELINE_STAGES.map((s) => s.key);
          const currentStageIndex = stageKeys.indexOf(pipelineStage);

          // Returns a count annotation shown below each stage dot.
          const getStageCount = (stageKey: string): string | null => {
            const idx = stageKeys.indexOf(stageKey as PipelineStage);
            const isPast = pipelineStage === 'done' || currentStageIndex > idx;
            const isActive = pipelineStage === stageKey;
            if (!isPast && !isActive) return null;
            if (stageKey === 'parsing') {
              // Show total CSV rows. If fewer are queued (fingerprint filter), note it.
              if (uploadCsvTotal != null) {
                const queueNote = uploadTotal != null && uploadTotal < uploadCsvTotal
                  ? ` (${uploadTotal.toLocaleString()} to process)`
                  : '';
                return `${uploadCsvTotal.toLocaleString()} records${queueNote}`;
              }
              return uploadTotal != null ? `${uploadTotal.toLocaleString()} records` : null;
            }
            if (stageKey === 'enriching') {
              // Use exec-only count as denominator; fall back to queue total if not yet available.
              const enrichDenom = uploadEnrichCount ?? uploadTotal;
              if (isActive && enrichDenom != null) {
                const done = uploadProcessed ?? 0;
                const batchHint = llmBatchCompleted != null && llmBatchTotal != null
                  ? ` · batch ${llmBatchCompleted}/${llmBatchTotal}`
                  : '';
                return `${done.toLocaleString()} / ${enrichDenom.toLocaleString()}${batchHint}`;
              }
              if (isPast && uploadProcessed != null) {
                return `${uploadProcessed.toLocaleString()} done`;
              }
            }
            if (stageKey === 'saving') {
              if (isActive && firestoreTotal != null && firestoreTotal > 0) {
                const done = firestoreSaved ?? 0;
                return `${done.toLocaleString()} / ${firestoreTotal.toLocaleString()}`;
              }
              if (isActive) return 'saving…';
              if (isPast && firestoreSaved != null) return `${firestoreSaved.toLocaleString()} saved`;
              if (isPast && uploadProcessed != null) return `${uploadProcessed.toLocaleString()} saved`;
            }
            if (stageKey === 'scores') {
              if (isActive) return 'scoring…';
            }
            return null;
          };

          // During enrichment: big live counter. Other stages: smaller stage-aware line.
          const isEnriching = pipelineStage === 'enriching';
          const counterLine = (uploadCsvTotal ?? uploadTotal) != null ? (() => {
            const csvTot = uploadCsvTotal ?? uploadTotal!;
            const queueTot = uploadTotal ?? uploadCsvTotal!;
            const enrichDenom = uploadEnrichCount ?? queueTot;
            const n = (uploadProcessed ?? 0).toLocaleString();
            if (pipelineStage === 'parsing') {
              const queueNote = queueTot < csvTot
                ? ` (${queueTot.toLocaleString()} new/changed to process)`
                : '';
              return `${csvTot.toLocaleString()} records in CSV${queueNote}`;
            }
            if (pipelineStage === 'enriching') return `${n} of ${enrichDenom.toLocaleString()} exec records enriched`;
            if (pipelineStage === 'saving') {
              if (firestoreTotal != null && firestoreTotal > 0) {
                const done = firestoreSaved ?? 0;
                return `${done.toLocaleString()} of ${firestoreTotal.toLocaleString()} records saved to Firestore`;
              }
              return `${n} records enriched — saving to Firestore…`;
            }
            if (pipelineStage === 'scores') return `${(uploadProcessed ?? queueTot).toLocaleString()} records saved — scoring companies…`;
            return `${n} of ${enrichDenom.toLocaleString()} records processed`;
          })() : null;

          return (
            <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg px-4 py-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-amber-500 border-t-transparent shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-amber-700 uppercase tracking-wide">
                      Processing in background
                    </p>
                    {counterLine && (
                      <p className={`font-bold text-amber-900 leading-snug ${
                        isEnriching ? 'text-xl mt-0.5' : 'text-sm mt-0.5'
                      }`}>
                        {counterLine}
                      </p>
                    )}
                    {asyncMessage && (
                      <p className="text-xs text-amber-600 mt-0.5">{asyncMessage}</p>
                    )}
                  </div>
                </div>
                <p className="text-lg font-bold text-amber-700 shrink-0">{Math.round(asyncProgress)}%</p>
              </div>
              <div className="mt-3">
                <div className="w-full bg-amber-100 rounded-full h-2.5">
                  <div
                    className="bg-amber-500 h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${Math.max(5, asyncProgress)}%` }}
                  />
                </div>
                <div className="mt-3 flex items-center justify-between gap-2">
                  {PIPELINE_STAGES.map((stage, index) => {
                    const isActive = pipelineStage === stage.key;
                    const isPast = pipelineStage === 'done' ? true : currentStageIndex > index;
                    const count = getStageCount(stage.key);
                    return (
                      <React.Fragment key={`async-${stage.key}`}>
                        <div className="flex flex-col items-center min-w-0 flex-1">
                          <div
                            className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                              pipelineStage === 'done'
                                ? 'bg-green-500'
                                : isActive
                                ? 'bg-amber-600 ring-2 ring-amber-200'
                                : isPast
                                ? 'bg-green-500'
                                : 'bg-amber-200'
                            }`}
                          />
                          <span className={`mt-1 text-[11px] text-center leading-tight ${isActive ? 'text-amber-800 font-medium' : isPast ? 'text-green-700' : 'text-amber-700'}`}>
                            {stage.label}
                          </span>
                          {count && (
                            <span className={`text-[10px] text-center font-semibold mt-0.5 leading-tight ${isPast && !isActive ? 'text-green-700' : 'text-amber-700'}`}>
                              {count}
                            </span>
                          )}
                        </div>
                        {index < PIPELINE_STAGES.length - 1 && (
                          <div className={`flex-1 h-0.5 min-w-[10px] max-w-[20px] rounded self-start mt-1.5 ${pipelineStage === 'done' || currentStageIndex > index ? 'bg-green-400' : 'bg-amber-200'}`} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
              <p className="text-sm text-amber-700 mt-3">
                You can leave or reload this page. We&apos;ll notify by email when done.
              </p>
              {asyncDiagnostics && (
                <p className="text-xs text-amber-700 mt-2">{asyncDiagnostics}</p>
              )}
            </div>
          );
        })()}

        {currentView === 'upload' && (
          <div className="space-y-4">
            {/* Header */}
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-900 mb-2">
                Upload LinkedIn Connections
              </h1>
              <p className="text-lg text-gray-600">
                Upload your LinkedIn connections CSV to analyze and score your network
              </p>
            </div>

            {/* Upload Card */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="space-y-6">
                {/* Limit Selector — admin only; regular users always upload all */}
                {isAdmin && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Number of Connections to Analyze
                    </label>
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        type="number"
                        min={0}
                        max={10000}
                        value={limit === 0 ? '' : limit}
                        onChange={(e) => {
                          const v = e.target.value;
                          setLimit(v === '' ? 0 : Math.max(0, Math.min(10000, parseInt(v, 10) || 0)));
                        }}
                        placeholder="all (0 = no limit)"
                        className="w-32 px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-ts-gold disabled:opacity-50"
                        disabled={uploading}
                      />
                      <span className="text-sm text-gray-500">or quick select:</span>
                      {[20, 40, 60, 100, 200, 500].map((n) => (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setLimit(n)}
                          disabled={uploading}
                          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-800 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-ts-gold disabled:opacity-50"
                        >
                          {n}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => setLimit(0)}
                        disabled={uploading}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-800 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-ts-gold disabled:opacity-50"
                      >
                        All
                      </button>
                    </div>
                    <p className="mt-1 text-sm text-gray-500">
                      Type a number (e.g. 40) or use quick select. Already-analyzed connections are skipped. 0 = process all new connections.
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Completion email (optional)
                  </label>
                  <input
                    type="email"
                    value={notifyEmail}
                    onChange={(e) => setNotifyEmail(e.target.value)}
                    placeholder="you@example.com"
                    disabled={uploading || !!asyncJobId}
                    className="w-full md:w-[28rem] px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-ts-gold disabled:opacity-50"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    When async processing finishes, we send a completion email here.
                  </p>
                </div>

                {/* File Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select CSV File
                  </label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-blue-400 transition-colors">
                    <div className="space-y-1 text-center">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                        aria-hidden="true"
                      >
                        <path
                          d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <div className="flex text-sm text-gray-600">
                        <label
                          htmlFor="file-upload"
                          className="relative cursor-pointer bg-white rounded-md font-medium text-ts-gold hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                        >
                          <span>Upload a file</span>
                          <input
                            id="file-upload"
                            name="file-upload"
                            type="file"
                            className="sr-only"
                            accept=".csv"
                            onChange={handleFileSelect}
                            disabled={uploading}
                          />
                        </label>
                      </div>
                      <p className="text-xs text-gray-500">CSV up to 10MB</p>
                    </div>
                  </div>
                  {file && (
                    <p className="mt-2 text-sm text-gray-600">
                      Selected: <span className="font-medium">{file.name}</span> (
                      {(file.size / 1024).toFixed(2)} KB)
                    </p>
                  )}
                </div>

                {/* Dotted timeline: Parsing → LLM enrichment → Firestore → Scores */}
                {(uploading || processingScores || pipelineStage === 'done') && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-5">
                    <div className="flex items-center justify-between gap-2">
                      {PIPELINE_STAGES.map((stage, index) => {
                        const currentIndex = PIPELINE_STAGES.map((s) => s.key).indexOf(pipelineStage);
                        const isActive = pipelineStage === stage.key;
                        const isPast = pipelineStage === 'done' ? true : currentIndex > index;
                        return (
                          <React.Fragment key={stage.key}>
                            <div className="flex flex-col items-center min-w-0 flex-1">
                              <div
                                className={`w-3 h-3 rounded-full flex-shrink-0 transition-colors ${
                                  pipelineStage === 'done'
                                    ? 'bg-green-500'
                                    : isActive
                                    ? 'bg-ts-navy ring-4 ring-blue-200'
                                    : isPast || (stage.key === 'parsing' && pipelineStage !== 'idle' && pipelineStage !== 'parsing')
                                    ? 'bg-green-500'
                                    : 'bg-gray-300'
                                }`}
                              />
                              <span
                                className={`mt-2 text-xs font-medium text-center max-w-[4.5rem] truncate ${
                                  isActive
                                    ? 'text-ts-gold'
                                    : isPast || pipelineStage === 'done'
                                      ? 'text-green-700'
                                      : 'text-gray-500'
                                }`}
                              >
                                {stage.label}
                              </span>
                            </div>
                            {index < PIPELINE_STAGES.length - 1 && (
                              <div
                                className={`flex-1 h-0.5 min-w-[12px] max-w-[24px] rounded ${
                                  pipelineStage === 'done' || currentIndex > index ? 'bg-green-400' : 'bg-gray-300'
                                }`}
                              />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                    {(uploading || processingScores) && pipelineStage !== 'done' && (
                      <p className="text-sm text-gray-600 mt-3 text-center">
                        {pipelineStage === 'parsing' && 'Parsing CSV...'}
                        {pipelineStage === 'enriching' && 'Enriching connections with LLM...'}
                        {pipelineStage === 'saving' && 'Saving to Firestore...'}
                        {pipelineStage === 'scores' && 'Calculating company scores...'}
                      </p>
                    )}
                  </div>
                )}

                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    {error}
                  </div>
                )}

                {/* Success Message */}
                {success && (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
                    {success}
                  </div>
                )}

                {/* Upload Button */}
                <div className="flex justify-end">
                  <button
                    onClick={handleUpload}
                    disabled={!file || uploading || processingScores}
                    className="px-6 py-3 bg-ts-navy text-white font-medium rounded-lg hover:bg-ts-charcoal focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ts-gold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {uploading || processingScores ? 'Processing...' : 'Upload & Process'}
                  </button>
                </div>
              </div>
            </div>

            {/* Manage data: delete connections / scores */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Manage data</h2>
              <p className="text-sm text-gray-600 mb-4">
                Delete your connection records or company scores. Only documents belonging to you are removed; other users’ data is unchanged. Useful to re-upload a fresh CSV or clear your data manually.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => setDeleteConfirm('connections')}
                  disabled={uploading || deleting}
                  className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Delete all connections
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteConfirm('scores')}
                  disabled={uploading || deleting}
                  className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Delete all company scores
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteConfirm('all')}
                  disabled={uploading || deleting}
                  className="px-4 py-2 border border-red-500 bg-red-50 text-red-800 rounded-lg hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Delete everything
                </button>
              </div>
              {deleteMessage && (
                <p className="mt-3 text-sm text-green-700">{deleteMessage}</p>
              )}
            </div>

            {/* Confirmation modal */}
            {deleteConfirm && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" aria-modal="true" role="dialog">
                <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {deleteConfirmConfig[deleteConfirm].title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-6">
                    {deleteConfirmConfig[deleteConfirm].description}
                  </p>
                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setDeleteConfirm(null)}
                      disabled={deleting}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-ts-gold disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(deleteConfirm)}
                      disabled={deleting}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                    >
                      {deleting ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Instructions */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                How to fetch your LinkedIn connections
              </h2>
              <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                Fetch your connections through LinkedIn&apos;s{' '}
                <strong className="font-medium text-gray-800">Download My Data</strong> export (not
                from My Network). Follow the steps below.
              </p>
              <ol className="list-decimal list-inside space-y-3 text-gray-600">
                <li>
                  Open LinkedIn&apos;s{' '}
                  <a
                    href="https://www.linkedin.com/mypreferences/d/download-my-data"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-ts-gold hover:underline font-medium"
                  >
                    Download My Data
                  </a>{' '}
                  page directly, or go to{' '}
                  <strong className="font-medium text-gray-800">Settings</strong> →{' '}
                  <strong className="font-medium text-gray-800">Data Privacy</strong> →{' '}
                  <strong className="font-medium text-gray-800">Download My Data</strong>.
                </li>
                <li>Request your data export and include your connections (wording on LinkedIn may vary).</li>
                <li>
                  When your archive is ready, unzip it and upload the connections file (often named{' '}
                  <span className="font-mono text-xs">Connections.csv</span> — sometimes{' '}
                  <span className="font-mono text-xs">DMConnections.csv</span>). Do not upload the ZIP itself.
                </li>
                <li>Upload that CSV file here.</li>
              </ol>
              <p className="text-xs text-gray-500 mt-4 leading-relaxed border-t border-gray-100 pt-4">
                If upload fails with “invalid CSV” or “no connections,” common causes are uploading the ZIP instead of the
                CSV, a spreadsheet saved in the wrong format, or a file missing the standard LinkedIn columns. The uploader
                tries to skip intro text before the header row when it can still detect{' '}
                <span className="font-mono">First Name, Last Name, URL, Company, Position, Connected On</span>.
              </p>
            </div>
          </div>
        )}

        {currentView === 'connections' && <ConnectionsListView />}
        {currentView === 'companies' && <CompaniesToTargetView highlightCompany={highlightCompany} />}
      </div>
    </div>
  );
};

export default ConnectionsDashboard;

